import dayjs from 'dayjs';
import { formatCount, formatShareOrDash, periodChangePercent, sharePercent } from '@/utils/charts/format';

export const ENGINEERING_READ_SCOPES = ['read_charts_engineering', 'read_charts_engineering_own'];
export const ENGINEERING_MAX_RANGE_DAYS = 731;
export const ENGINEERING_DEFAULT_PRESET = 'quarter';

export const PULL_REQUEST_KINDS = [
    { value: 'feature', label: 'Feature' },
    { value: 'fix', label: 'Fix' },
    { value: 'refactor', label: 'Refactor' },
    { value: 'hotfix', label: 'Hotfix' },
    { value: 'test', label: 'Test' },
];

const KIND_LABELS = Object.fromEntries(PULL_REQUEST_KINDS.map((kind) => [kind.value, kind.label]));

export const KIND_COLORS = {
    feature: '#2563eb',
    fix: '#f59e0b',
    refactor: '#8b5cf6',
    hotfix: '#dc2626',
    test: '#9ca3af',
};

const STATE_LABELS = { open: 'Open', merged: 'Merged', closed: 'Closed' };

const REVIEW_STATUS_LABELS = {
    draft: 'Draft',
    no_reviewers: 'No reviewers',
    waiting: 'Waiting for review',
    changes_requested: 'Changes requested',
    approved: 'Approved',
};

export const reviewStatusLabel = (status) => REVIEW_STATUS_LABELS[status] || status || '—';

export const kindLabel = (kind) => KIND_LABELS[kind] || kind || '—';

export const stateLabel = (state) => STATE_LABELS[state] || state || '—';

export const formatDays = (value) => {
    if (value === null || value === undefined) {
        return '—';
    }
    if (value < 1) {
        return `${Math.round(value * 24)} h`;
    }
    return `${Number(value).toFixed(1)} d`;
};

export const formatPct = (value) => {
    if (value === null || value === undefined) {
        return '—';
    }
    return `${value}%`;
};

export const formatDay = (value) => {
    const parsed = dayjs(value);
    if (value && parsed.isValid()) {
        return parsed.format('MMM D, YYYY');
    }
    return '—';
};

const formatPeriod = (period, grain) => {
    if (grain === 'week') {
        return dayjs(period).format('MMM D');
    }
    return dayjs(`${period}-01`).format('MMM YYYY');
};

export const formatRange = (range) => {
    const from = dayjs(range.from);
    const to = dayjs(range.to);
    if (from.year() === to.year()) {
        return `${from.format('MMM D')} – ${to.format('MMM D, YYYY')}`;
    }
    return `${from.format('MMM D, YYYY')} – ${to.format('MMM D, YYYY')}`;
};

export const periodComparisonLabel = (report) => {
    if (!report.range || !report.previous_range) {
        return '';
    }
    return `${formatRange(report.range)}, compared with ${formatRange(report.previous_range)}`;
};

export const plural = (count, singular, many = `${singular}s`) =>
    `${formatCount(count)} ${count === 1 ? singular : many}`;

export const formatDateTime = (value) => {
    if (!value) {
        return 'Never';
    }
    return dayjs(value).format('MMM D, YYYY h:mm A');
};

export const syncStatusLabel = (repositories) => {
    const active = repositories.filter((repository) => repository.active);
    if (!active.length) {
        return '';
    }
    const failed = active.filter((repository) => repository.last_sync_error).length;
    if (failed) {
        return `${failed} of ${active.length} repositories failed the last sync.`;
    }
    const synced = active.map((repository) => repository.last_synced_at);
    if (synced.some((value) => !value)) {
        return 'Some repositories have not been synced yet.';
    }
    const oldest = [...synced].sort((left, right) => new Date(left) - new Date(right))[0];
    return `Last synced ${formatDateTime(oldest)}.`;
};

export const emptyPullRequestSummary = () => ({
    opened: 0,
    merged: 0,
    released: 0,
    open: 0,
    closed_unmerged: 0,
    ttr_median_days: null,
    ttr_p90_days: null,
    merge_to_release_median_days: null,
    kinds: { feature: 0, fix: 0, refactor: 0, hotfix: 0, test: 0 },
    corrective: 0,
    corrective_pct: null,
    features_merged: 0,
    features_with_corrections: 0,
    features_with_corrections_pct: null,
    additions: 0,
    deletions: 0,
});

export const emptyReviewSummary = () => ({
    reviewed_prs: 0,
    approved: 0,
    changes_requested: 0,
    comment_only: 0,
    submissions: 0,
    comments: 0,
    by_author: [],
});

export const emptyFeedbackSummary = () => ({
    opened: 0,
    reviewed_prs: 0,
    comments: 0,
    comments_per_pr: null,
    changes_requested: 0,
    prs_with_changes_requested: 0,
    prs_with_changes_requested_pct: null,
    approved_first_pass: 0,
    approved_first_pass_pct: null,
    first_review_median_days: null,
    resolved_prs: 0,
    resolution_median_days: null,
    resolution_p90_days: null,
});

const emptyPeriod = () => ({
    pull_requests: emptyPullRequestSummary(),
    reviews: emptyReviewSummary(),
    feedback: emptyFeedbackSummary(),
});

export const emptyOpenWork = () => ({
    open_prs: 0,
    drafts: 0,
    oldest_open_days: null,
    pending_reviews: 0,
    oldest_pending_days: null,
});

export const emptyScorecard = () => ({ met: 0, scored: 0, targets: [] });

export const emptyTeamReport = () => ({
    team: { current: emptyPeriod(), previous: emptyPeriod(), targets: emptyScorecard() },
    trend_grain: 'month',
    trend: [],
    developers: [],
});

export const emptyDeveloperReport = () => ({
    developer: null,
    current: emptyPeriod(),
    previous: emptyPeriod(),
    trend_grain: 'month',
    trend: [],
    corrected_features: [],
    pull_requests: [],
    targets: emptyScorecard(),
    open_work: emptyOpenWork(),
    open_pull_requests: [],
    pending_reviews: [],
});

export const openWorkHint = (repositories) =>
    `Right now, not limited to the selected dates. Draft pull requests are not counted until they are ready for review. ${syncStatusLabel(repositories)}`.trim();

const change = (current, previous, invert = false) => ({
    current: current ?? 0,
    previous: previous ?? 0,
    invert,
});

export const engineeringKpis = (current, previous) => {
    const prs = current.pull_requests;
    const previous_prs = previous.pull_requests;
    const reviews = current.reviews;
    return [
        {
            key: 'released',
            label: 'Released to production',
            value: formatCount(prs.released),
            footer: `${formatCount(prs.merged)} merged · ${formatCount(prs.opened)} opened`,
            change: change(prs.released, previous_prs.released),
        },
        {
            key: 'ttr',
            label: 'Median time to release',
            value: formatDays(prs.ttr_median_days),
            footer: `p90 ${formatDays(prs.ttr_p90_days)} · merge to prod ${formatDays(prs.merge_to_release_median_days)}`,
            change: change(prs.ttr_median_days, previous_prs.ttr_median_days, true),
        },
        {
            key: 'corrective',
            label: 'Corrective work',
            value: formatPct(prs.corrective_pct),
            footer: `${plural(prs.kinds.fix, 'fix', 'fixes')} · ${plural(prs.kinds.refactor, 'refactor')} · ${plural(prs.kinds.hotfix, 'hotfix', 'hotfixes')}`,
            change: change(prs.corrective_pct, previous_prs.corrective_pct, true),
        },
        {
            key: 'features_corrected',
            label: 'Features that needed a fix',
            value: formatPct(prs.features_with_corrections_pct),
            footer: `${formatCount(prs.features_with_corrections)} of ${plural(prs.features_merged, 'merged feature')}`,
            change: change(prs.features_with_corrections_pct, previous_prs.features_with_corrections_pct, true),
        },
        {
            key: 'reviews',
            label: 'PRs reviewed',
            value: formatCount(reviews.reviewed_prs),
            footer: `${formatCount(reviews.approved)} approved · ${formatCount(reviews.changes_requested)} changes requested · ${formatCount(reviews.comment_only)} comments only`,
            change: change(reviews.reviewed_prs, previous.reviews.reviewed_prs),
        },
    ];
};

// Rates and medians from fewer pull requests than this swing too much to compare between periods.
export const MIN_COMPARABLE_SAMPLE = 3;

const ofReviewed = (part, summary) => `${formatCount(part)} of ${plural(summary.reviewed_prs, 'reviewed PR')}`;

const FEEDBACK_METRICS = [
    {
        key: 'reviewed',
        label: 'Pull requests that got a review',
        description: 'Opened in the period and reviewed at least once by someone else.',
        unit: 'pct',
        number: (feedback) => sharePercent(feedback.reviewed_prs, feedback.opened),
        value: (feedback) => formatShareOrDash(feedback.reviewed_prs, feedback.opened),
        detail: (feedback) => `${formatCount(feedback.reviewed_prs)} of ${plural(feedback.opened, 'opened PR')}`,
        sample: (feedback) => feedback.opened,
        empty: 'No PRs opened',
    },
    {
        key: 'comments',
        label: 'Review comments received',
        description: 'Inline and conversation comments from reviewers.',
        unit: 'count',
        number: (feedback) => feedback.comments,
        value: (feedback) => formatCount(feedback.comments),
        detail: (feedback) => (feedback.reviewed_prs ? `${feedback.comments_per_pr.toFixed(1)} per reviewed PR` : 'No reviewed PRs'),
    },
    {
        key: 'changes_requested',
        label: 'Changes requested',
        description: 'Share of reviewed PRs where a reviewer requested changes.',
        unit: 'pct',
        better: 'down',
        number: (feedback) => feedback.prs_with_changes_requested_pct,
        value: (feedback) => formatPct(feedback.prs_with_changes_requested_pct),
        detail: (feedback) => ofReviewed(feedback.prs_with_changes_requested, feedback),
        sample: (feedback) => feedback.reviewed_prs,
        empty: 'No reviewed PRs',
    },
    {
        key: 'first_pass',
        label: 'Approved without changes',
        description: 'Share of reviewed PRs approved with no change request.',
        unit: 'pct',
        better: 'up',
        number: (feedback) => feedback.approved_first_pass_pct,
        value: (feedback) => formatPct(feedback.approved_first_pass_pct),
        detail: (feedback) => ofReviewed(feedback.approved_first_pass, feedback),
        sample: (feedback) => feedback.reviewed_prs,
        empty: 'No reviewed PRs',
    },
    {
        key: 'first_review',
        label: 'Time to first review',
        description: 'Median wait from opening the PR to its first review. Depends on the reviewers.',
        unit: 'time',
        number: (feedback) => feedback.first_review_median_days,
        value: (feedback) => formatDays(feedback.first_review_median_days),
        detail: (feedback) => `Median of ${plural(feedback.reviewed_prs, 'PR')}`,
        sample: (feedback) => feedback.reviewed_prs,
        empty: 'No reviewed PRs',
    },
    {
        key: 'resolution',
        label: 'Time to resolve feedback',
        description: 'Median time from the first change request or inline comment until approval or merge.',
        unit: 'time',
        better: 'down',
        number: (feedback) => feedback.resolution_median_days,
        value: (feedback) => formatDays(feedback.resolution_median_days),
        detail: (feedback) => `p90 ${formatDays(feedback.resolution_p90_days)} · ${plural(feedback.resolved_prs, 'PR')}`,
        sample: (feedback) => feedback.resolved_prs,
        empty: 'No resolved feedback',
    },
];

const outcomeMetric = (key, label) => ({
    key,
    label,
    unit: 'pct',
    number: (reviews) => sharePercent(reviews[key], reviews.reviewed_prs),
    value: (reviews) => formatShareOrDash(reviews[key], reviews.reviewed_prs),
    detail: (reviews) => ofReviewed(reviews[key], reviews),
    sample: (reviews) => reviews.reviewed_prs,
    empty: 'No reviews',
});

const REVIEWS_GIVEN_METRICS = [
    {
        key: 'reviewed',
        label: 'Pull requests reviewed',
        unit: 'count',
        number: (reviews) => reviews.reviewed_prs,
        value: (reviews) => formatCount(reviews.reviewed_prs),
        detail: (reviews) => `${plural(reviews.submissions, 'review')} submitted`,
    },
    outcomeMetric('approved', 'Approved'),
    outcomeMetric('changes_requested', 'Changes requested'),
    outcomeMetric('comment_only', 'Comments only'),
    {
        key: 'comments',
        label: 'Inline comments written',
        unit: 'count',
        number: (reviews) => reviews.comments,
        value: (reviews) => formatCount(reviews.comments),
        detail: (reviews) => (reviews.reviewed_prs ? `${(reviews.comments / reviews.reviewed_prs).toFixed(1)} per reviewed PR` : 'No reviews'),
    },
];

const isComparable = (metric, current, previous) => {
    if (metric.number(current) === null || metric.number(previous) === null) {
        return false;
    }
    return !metric.sample || Math.min(metric.sample(current), metric.sample(previous)) >= MIN_COMPARABLE_SAMPLE;
};

const pointsChange = (delta) => {
    const rounded = Math.round(delta * 10) / 10;
    if (rounded === 0) {
        return 'No change';
    }
    return `${rounded > 0 ? '+' : '-'}${Math.abs(rounded).toFixed(1)} pts`;
};

const relativeChange = (current, previous) => {
    if (previous === 0) {
        return current === 0 ? 'No change' : 'New';
    }
    return periodChangePercent(current, previous)?.text ?? 'No change';
};

const changeTone = (delta, better) => {
    if (!better || delta === 0) {
        return 'neutral';
    }
    return (delta > 0) === (better === 'up') ? 'better' : 'worse';
};

const metricChange = (metric, current, previous) => {
    if (!isComparable(metric, current, previous)) {
        return { text: 'Too few PRs', tone: 'none' };
    }
    const now = metric.number(current);
    const before = metric.number(previous);
    const text = metric.unit === 'pct' ? pointsChange(now - before) : relativeChange(now, before);
    return { text, tone: changeTone(now - before, metric.better) };
};

const comparisonCell = (metric, summary) => {
    if (metric.sample && !metric.sample(summary)) {
        return { value: '—', detail: metric.empty };
    }
    return { value: metric.value(summary), detail: metric.detail(summary) };
};

const comparisonRows = (metrics, current, previous) => metrics.map((metric) => ({
    id: metric.key,
    name: metric.label,
    description: metric.description,
    current: comparisonCell(metric, current),
    previous: comparisonCell(metric, previous),
    change: metricChange(metric, current, previous),
}));

export const feedbackRows = (current, previous) => comparisonRows(FEEDBACK_METRICS, current, previous);

export const reviewsGivenRows = (current, previous) => comparisonRows(REVIEWS_GIVEN_METRICS, current, previous);

const formatRangeShort = (range) => {
    if (!range) {
        return '';
    }
    const from = dayjs(range.from);
    const to = dayjs(range.to);
    if (from.year() === to.year()) {
        return `${from.format('MMM D')} – ${to.format('MMM D')}`;
    }
    return `${from.format('MMM D, YYYY')} – ${to.format('MMM D, YYYY')}`;
};

export const comparisonColumns = (report) => [
    { key: 'name', label: 'Metric', align: 'start' },
    { key: 'current', label: formatRangeShort(report.range) || 'This period' },
    { key: 'previous', label: formatRangeShort(report.previous_range) || 'Previous period' },
    { key: 'change', label: 'Change' },
];

const TARGET_DEFINITIONS = {
    time_to_release: {
        label: 'Median time to release',
        source: 'DORA lead time for changes: elite teams release in under a day, high performers in under a week.',
        unit: 'time',
        detail: (sample) => `Median of ${plural(sample, 'released PR')}`,
    },
    change_failure: {
        label: 'Features that needed a fix',
        source: 'DORA change failure rate: high performers stay at 15% or less.',
        unit: 'pct',
        detail: (sample) => `Of ${plural(sample, 'merged feature')}`,
    },
    reviewed_before_merge: {
        label: 'Merged after a review',
        source: 'PCI DSS 6.2.3: every change to payment software is reviewed by someone other than its author before production.',
        unit: 'pct',
        detail: (sample) => `Of ${plural(sample, 'merged PR')}`,
    },
    first_review: {
        label: 'Time to first review',
        source: 'Google engineering practices: respond to a review request within one business day.',
        unit: 'time',
        detail: (sample) => `Median of ${plural(sample, 'reviewed PR')}`,
    },
    pending_review_wait: {
        label: 'Longest pending review',
        source: 'Google engineering practices: nobody should wait more than one business day for a review.',
        unit: 'time',
        detail: (sample) => (sample ? `${formatCount(sample)} waiting now` : 'Nothing waiting now'),
    },
};

export const TARGETS_HINT = `Meets is on target, Close is inside the outer band, Misses is beyond it. Targets measured on fewer than ${MIN_COMPARABLE_SAMPLE} pull requests are not scored. `
    + 'Waits are measured in calendar days, so a review that spans a weekend can show Close.';

const TARGET_STATUSES = {
    met: { text: 'Meets', tone: 'better' },
    close: { text: 'Close', tone: 'close' },
    missed: { text: 'Misses', tone: 'worse' },
    no_data: { text: 'Too few PRs', tone: 'none' },
};

const formatThreshold = (value, unit) => {
    if (unit === 'pct') {
        return `${value}%`;
    }
    return plural(value, 'day');
};

const thresholdText = (target, value, unit) => {
    if (target.better === 'up' && value === 100) {
        return '100%';
    }
    return `${target.better === 'up' ? '≥' : '≤'} ${formatThreshold(value, unit)}`;
};

const formatTargetValue = (value, unit) => (unit === 'pct' ? formatPct(value) : formatDays(value));

export const targetsMetLabel = (scorecard) => {
    if (!scorecard.scored) {
        return 'No targets with enough data';
    }
    return `${scorecard.met} of ${plural(scorecard.scored, 'target')} met`;
};

const developersMeeting = (counts) => (counts?.scored ? `${counts.met} of ${counts.scored}` : '—');

export const targetRows = (scorecard) => scorecard.targets.map((target) => {
    const definition = TARGET_DEFINITIONS[target.key];
    return {
        id: target.key,
        name: definition.label,
        description: definition.source,
        target: {
            value: thresholdText(target, target.target, definition.unit),
            detail: `Close at ${thresholdText(target, target.close, definition.unit)}`,
        },
        current: { value: formatTargetValue(target.value, definition.unit), detail: definition.detail(target.sample) },
        status: TARGET_STATUSES[target.status],
        developers: developersMeeting(target.developers),
    };
});

export const targetColumns = (report, { team = false } = {}) => {
    const columns = [
        { key: 'name', label: 'Metric', align: 'start' },
        { key: 'target', label: 'Target' },
        { key: 'current', label: formatRangeShort(report.range) || 'This period' },
        { key: 'status', label: 'Status' },
    ];
    if (team) {
        columns.push({ key: 'developers', label: 'Developers meeting it' });
    }
    return columns;
};

export const trendLabels = (trend, grain) => trend.map((bucket) => formatPeriod(bucket.period, grain));

export const kindSeries = (trend) => PULL_REQUEST_KINDS.map((kind) => ({
    label: kind.label,
    color: KIND_COLORS[kind.value],
    data: trend.map((bucket) => bucket.kinds[kind.value] || 0),
}));

export const ttrSeries = (trend) => [
    {
        label: 'Median time to release',
        color: KIND_COLORS.feature,
        data: trend.map((bucket) => bucket.ttr_median_days),
    },
];

export const pullRequestUrl = (row, number) => row.url.replace(/\/pull\/\d+$/, `/pull/${number}`);
